import React, { useRef, useState } from 'react';

import { PhotoCameraRounded } from '@mui/icons-material';
import { Fade, IconButton } from '@mui/material';
import { CirclePicker } from 'react-color';

import { DEFAULT_PROFILE_COLOR, PROFILE_COLOR_OPTIONS } from '../../utils/common/constants';
import Modal from '../Modal';
import { ProfileIconProps } from '../ProfileIcon';
import { ColorHex } from '../ProfileIcon/types';

import {
  BaseProfileIcon,
  EditOverlay,
  HiddenInput,
  ModalContainer,
  ModalDivider,
  ProfileContainer,
  SmallProfileIcon,
  UploadIcon,
  UploadImage,
} from './index.styles';

interface EditableProfileIconProps extends ProfileIconProps {
  onUpdate?: (image?: File, color?: ColorHex) => void,
}

const EditableProfileIcon = ({
  onUpdate = () => {},
  ...rest
}: EditableProfileIconProps) => {
  const originalColor = rest?.color || DEFAULT_PROFILE_COLOR;

  const [isEditShown, setIsEditShown] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [newColor, setNewColor] = useState<ColorHex | undefined>(undefined);
  const [savedColor, setSavedColor] = useState<ColorHex | undefined>(originalColor);
  const [selectedImage, setSelectedImage] = useState<File>();

  const hiddenInput = useRef<HTMLInputElement>(null);

  const handleChangeComplete = (selectedColor: any) => {
    setNewColor(selectedColor.hex);
    setSelectedImage(undefined);
  };

  const handleSave = () => {
    if (newColor) setSavedColor(newColor);
    onUpdate(selectedImage, newColor);
    setModalOpen(false);
  };

  const handleModalClose = () => {
    setNewColor(undefined);
    setSelectedImage(undefined);
    setModalOpen(false);
  };

  const handleClickUpload = () => {
    hiddenInput.current?.click();
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target?.files) {
      setSelectedImage(event.target.files[0]);
      setNewColor(undefined);
    }
  };

  const getSelectedImageUrl = () => {
    if (newColor) return '';
    if (selectedImage) return URL.createObjectURL(selectedImage);
    return rest?.imageUrl;
  };

  return (
    <>
      <Modal
        title="Edit Profile Icon"
        titleColor="darkPrimary"
        open={modalOpen}
        handleClose={handleModalClose}
        buttons={[
          {
            label: 'Save',
            onClick: handleSave,
            color: 'darkPrimary',
          },
        ]}
      >
        <ModalContainer>
          <SmallProfileIcon
            {...rest}
            imageUrl={getSelectedImageUrl()}
            color={newColor || savedColor}
            size={108}
          />
          <CirclePicker
            color={newColor}
            onChange={handleChangeComplete}
            colors={PROFILE_COLOR_OPTIONS}
          />
          <ModalDivider orientation="horizontal" variant="middle"> OR </ModalDivider>
          <UploadImage>
            <IconButton onClick={handleClickUpload}>
              <UploadIcon />
            </IconButton>
            <HiddenInput
              ref={hiddenInput}
              type="file"
              name="myImage"
              onChange={(event) => {
                handleImageUpload(event);
              }}
              accept="image/*"
            />
          </UploadImage>
        </ModalContainer>
      </Modal>
      <ProfileContainer
        onMouseEnter={() => setIsEditShown(true)}
        onMouseLeave={() => setIsEditShown(false)}
        onClick={() => setModalOpen(true)}
      >
        <BaseProfileIcon
          {...rest}
          color={savedColor}
        />
        <Fade in={isEditShown}>
          <EditOverlay>
            <PhotoCameraRounded />
          </EditOverlay>
        </Fade>
      </ProfileContainer>
    </>
  );
};

export default EditableProfileIcon;
